/**
 * OPTIMA's avatar, «La scatola» (the design team's handoff, direction 1d; ADR 9077).
 *
 * A red Bata box with the Bata logo, from which a shoe comes out and becomes the avatar. One inline
 * SVG, with the state on its root as `data-state` and the classes the stylesheet moves and colours
 * (`src/styles/optima-avatar.css`: `carton`, `lid`, `shoe`, `dash`, `anim`, `solid`, `ghost`, `tag`).
 * `carton` is the delivered `box`: Tailwind owns a `box-` utility family, and `lint:dead-classes`
 * reads a bare `box` as one. The geometry is the delivered `optima-box-live` SVG's; the logo path is copied exactly and is never
 * redrawn or recoloured. No colour lives here: the fills are classes over tokens, so the two themes
 * are the token pair and not two copies of the picture.
 *
 *   pronto    the closed box          vuota    the open, empty box (the entry in the bar while the
 *   ascolto   the shoe, still                  shoe is in the panel)
 *   leggo     the toe taps            nonso    the shoe hatched, the sole dashed
 *   rispondo  walks, with dashes      bozza    a tag on the laces, pulsing
 *
 * `staticState` is for the avatar beside a finished answer: it never moves and never transitions.
 * Reduced motion is the stylesheet's, and gives every state its still pose.
 *
 * The picture is an image with a name per state (`optima.state.*`), unless it sits beside text that
 * already says it (`decorative`), when it is hidden from assistive technology.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { OPTIMA_STATE_KEYS, type OptimaAvatarState } from './optima';

export interface OptimaAvatarProps {
  readonly state: OptimaAvatarState;
  /** In CSS pixels. Never under 16: the box stops reading as a box. */
  readonly size: number;
  /** Beside a finished answer: no loop and no transition, whatever the state. */
  readonly staticState?: boolean;
  /** Beside a visible «OPTIMA»: hidden from assistive technology, so the name is not read twice. */
  readonly decorative?: boolean;
  readonly className?: string;
}

/** The smallest the avatar is drawn (LEGGIMI: «mai sotto i 16px»). */
export const OPTIMA_AVATAR_MIN_SIZE = 16;

/** The Bata logo, from the delivered SVG, byte for byte. */
const LOGO_PATH = 'M135.5,29.181c1.945-10.229,13.989-15.416,22.876-14.922v0.001 c3.434,0.194,6.395,1.236,8.114,3.166l0.012-0.012c0.004,0.004,0.008,0.008,0.011,0.012c0.706-0.748,1.413-1.495,2.126-2.237 c0.416-0.433,1.605-0.293,2.661-0.102c1.195,0.217,2.472,0.711,3.289,0.712c0.833,0,2.801-0.604,4.673-0.885 c1.871-0.28,3.646-0.237,3.341,0.519l-1.675,4.15h0.032l-5.475,13.542c-1.09,2.697,3.179,1.982,9.109-4.291 c1.016,0.666,1.478,0.791,2.493,3.311c-5.066,4.354-9.902,7.482-16.605,9.791c-6.027,2.078-10.467-0.93-11.864-5.943 c-8.563,8.02-20.494,7.934-23.919-1.113c-5.567,2.752-12.222,5.676-14.982,6.473c-2.164,0.625-4.134,1.064-5.982,1.076 c-2.965,0.02-6.198-2.74-5.178-6.801c-4.91,2.174-8.81,3.592-14.436,5.529c-6.027,2.078-9.494-0.15-10.892-5.164 c-8.565,8.021-20.501,7.934-23.922-1.121c-3.363,1.619-8.017,3.998-12.3,5.676c-6.889,3.096-14.962,2.477-20.993-3.838l1.972-3.104 c2.839,3.02,6.302,2.85,9.016,0.398c2.89-2.611,4.724-7.646,3.409-11.771c-0.578-1.813-2.622-3.205-6.456-2.951l1.187-3.161 c3.92-0.139,6.986-0.662,8.678-3.126c2.376-3.461,2.417-8.794-2.681-8.65c-0.901,0.025-1.961,0.217-3.195,0.614 c-0.883,2.613-2.439,5.697-3.691,8.937c-2.857,7.393-6.057,15.599-9.774,20.888c-4.429,6.301-9.165,7.547-12.981,7.291 c-3.992-0.27-7.122-1.479-11.497-5.424c-0.021-1.674,0.146-2.402,0.464-4.48c1.531,2.156,6.807,4.451,8.517,1.943 c3.826-5.615,8.036-16.445,11.177-23.436c-2.839,0.942-5.474,2.67-9.196,5.251c0.044-0.428-1.242-1.366-1.191-2.625 c5.962-6.876,16.522-11.08,23.633-12.583c10.868-2.297,22.53,1.044,24.28,7.913c1.167,4.581-4.236,8.875-9.313,9.495 c2.922,1.72,6.023,4.707,6.8,8.361c0.392,1.844,0.284,3.824-0.3,5.719c1.708-0.793,3.772-1.834,5.132-2.387 c2.143-14.696,24.854-19.423,31.121-12.396l2.148-2.237c0.416-0.433,1.605-0.293,2.66-0.102c1.195,0.217,2.473,0.711,3.29,0.712 c0.833,0,2.801-0.604,4.672-0.885c1.872-0.28,3.646-0.237,3.341,0.519l-7.139,17.693c-1.614,3.994,10.238-3.084,10.937-3.467 l5.092-10.823l-5.244,0.073l0.942-4.448l5.794,0.003c0.953-1.265,1.235-2.784,2.5-3.736c1.716-1.292,5.63-0.598,7.429-1.772 c1.555-1.016,2.75-2.546,4.187-4.902c1.241-2.036,5.756-1.515,4.893,0.424l-4.04,10.104l5.105-0.048 c2.426,0.127,0.343,4.157-2.415,4.188l-4.218,0.077l-4.898,12.682C124.553,35.636,130.81,31.941,135.5,29.181L135.5,29.181z M88.601,20.504c-0.56-1.925-2.234-2.442-3.682-2.288c-3.429,0.366-6.742,4.172-8.442,6.82c-2.724,4.244-2.078,10.496,2.252,9.334 C83.31,33.138,89.414,23.3,88.601,20.504L88.601,20.504z M163.99,20.504c-0.561-1.925-2.234-2.442-3.683-2.288 c-3.429,0.366-6.741,4.172-8.442,6.82c-2.724,4.244-2.077,10.496,2.252,9.334C158.699,33.138,164.802,23.3,163.99,20.504 L163.99,20.504z';

/** The shoe's outline, shared by the solid shoe and the hatched one. */
const SHOE_PATH = 'M6 32V22H11L12.5 16H21L26 23L36 26C40 27 42 29 42 32Z';

export function OptimaAvatar({ state, size, staticState = false, decorative = false, className }: OptimaAvatarProps): React.JSX.Element {
  const { t } = useTranslation();
  // The hatch is a `<pattern>` and patterns are found by id: two avatars on one screen need two ids.
  const hatch = `optima-hatch-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
  const px = Math.max(OPTIMA_AVATAR_MIN_SIZE, size);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={px}
      height={px}
      data-state={state}
      data-static={staticState ? 'true' : 'false'}
      data-optima-avatar=""
      className={`optima-avatar${className === undefined ? '' : ` ${className}`}`}
      {...(decorative ? { 'aria-hidden': true, focusable: 'false' } : { role: 'img', 'aria-label': t(OPTIMA_STATE_KEYS[state]) })}
    >
      <g className="carton">
        <rect className="oa-box" x="6" y="24" width="36" height="17" />
        <g transform="translate(10.5 29.4) scale(0.14432)">
          <path className="oa-logo" d={LOGO_PATH} />
        </g>
      </g>
      <rect className="lid carton oa-ink" x="4" y="18" width="40" height="6" />
      <g className="shoe">
        <g transform="translate(0 -3)">
          <g className="dash">
            <rect className="oa-ink" x="0" y="22" width="4" height="1.6" />
            <rect className="oa-ink" x="1" y="27" width="3" height="1.6" />
          </g>
          <g className="anim">
            <g className="solid">
              <path className="oa-ink" d={SHOE_PATH} />
              <rect className="oa-hole" x="14.5" y="19.5" width="6" height="1.6" transform="rotate(20 17.5 20.3)" />
              <rect className="oa-hole" x="17" y="23" width="6" height="1.6" transform="rotate(20 20 23.8)" />
              <rect className="oa-accent" x="6" y="32" width="36" height="4" />
            </g>
            <g className="ghost">
              <defs>
                <pattern id={hatch} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect className="oa-ink" width="1.4" height="4" />
                </pattern>
              </defs>
              <path className="oa-outline" d={SHOE_PATH} fill={`url(#${hatch})`} />
              <rect className="oa-outline" x="6.8" y="32.8" width="34.4" height="2.4" fill="none" strokeDasharray="3 2" />
            </g>
            <g className="tag">
              <line className="oa-line" x1="18" y1="17" x2="15" y2="10" />
              <rect className="oa-accent" x="8" y="3.5" width="11" height="7" transform="rotate(-12 13.5 7)" />
              <circle className="oa-hole" cx="10.6" cy="7.4" r="1" />
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}
